import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export const CaseStudies = () => {
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/case-studies`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setStudies)
      .catch(() => setStudies([]));
  }, []);

  if (studies.length === 0) return null;

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative py-20 lg:py-32 bg-[#F3F4F8]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} className="max-w-2xl mb-14 lg:mb-20">
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-4">
            Case studies
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A] mb-5">
            Proof, not promises
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Real engagements, real numbers.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {studies.map((s, i) => (
            <motion.article
              key={s.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              data-testid={`case-study-card-${s.id}`}
              className="bento-card group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-7 lg:p-9"
            >
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#0FA89E] border border-[rgba(15,168,158,0.25)] rounded-full px-3 py-1">
                  {s.service}
                </span>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  {s.industry}
                </span>
              </div>
              {s.image_path && (
                <div className="rounded-xl overflow-hidden mb-6 h-48">
                  <img
                    src={`${BACKEND_URL}/api/files/${s.image_path}`}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    data-testid={`case-study-image-${s.id}`}
                  />
                </div>
              )}
              <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#0F172A] mb-2">
                {s.title}
              </h3>
              <p className="text-sm font-medium text-slate-500 mb-6">{s.client}</p>
              <div className="space-y-4 mb-8">
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
                    Challenge
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.challenge}</p>
                </div>
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">
                    Result
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.result}</p>
                </div>
              </div>
              <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-5">
                <span className="font-display text-lg sm:text-xl font-bold text-[#F5A400]">
                  {s.metric}
                </span>
                <ArrowUpRight className="w-5 h-5 text-[#0FA89E] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
